import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectIsLive,
  selectIsLiveChannel,
} from 'containers/Player/selectors/media.selector';
import { getTickLanguageParams } from '../utils/languagePreferences';
import {
  attachdevice,
  trackingEvent,
} from '../actions/tracking/tracking.actions';
import { useVodPaywayToken } from 'containers/VodPlayer/hooks/useVodPaywayToken';

const useTracking = (videoTagRef, deviceId, groupId) => {
  const dispatch = useDispatch();
  const isLive = useSelector(selectIsLiveChannel(groupId));
  const media = useSelector(state => {
    if (!state?.Player?.PictureInPicture?.showing) {
      return state?.Player?.Player?.data;
    } else {
      return state?.Player?.PictureInPicture?.media;
    }
  });
  const pbi = useSelector(state => state?.Player?.PBI?.data);
  const firstPlay = useRef(true);
  const initialTime = useRef(null);
  const tickInterval = useRef(null);
  const trackingData = useRef(null);
  const recordingSelected = useSelector(
    store => store?.Player?.RecordingSelected,
  );
  const currentAudioLanguage = useSelector(
    state => state.Player.OptionButtons.languageOptions.currentAudioLanguage,
  );
  const { playButton } = useVodPaywayToken(pbi);

  const handleAttach = () => {
    dispatch(
      attachdevice({
        group_id: media?.group?.common?.id,
        device_id: deviceId,
      }),
    );
  };

  const handleTick = (params = {}) => {
    dispatch(
      trackingEvent(media?.tracking?.urls?.tick, {
        timecode: getPlayingTime(),
        ...params,
      }),
    );
  };

  const handleStart = () => {
    const linealChannelData = JSON.parse(
      localStorage.getItem('linealChannelData') || '{}',
    );
    const found = linealChannelData?.find(
      paq =>
        paq?.groups?.indexOf(
          !recordingSelected
            ? media?.group?.common?.id
            : recordingSelected?.item?.id,
        ) > -1,
    );
    let purchaseid;
    if (found) purchaseid = found?.purchaseid;

    dispatch(
      trackingEvent(media?.tracking?.urls?.view, {
        purchase_id: playButton?.purchaseid || purchaseid || '',
        group_id: !recordingSelected
          ? media?.group?.common?.id
          : recordingSelected?.item?.id,
        device_id: deviceId,
      }),
    );
  };

  const handlePause = e => {
    if (e && e.target?.currentTime === e.target.duration) {
      return;
    }
    if (!isLive || recordingSelected) {
      dispatch(
        trackingEvent(media?.tracking?.urls?.pause, {
          timecode: getPlayingTime(),
        }),
      );
    }
  };

  const handleStop = time => {
    if (!media?.tracking?.urls?.stop) return;
    dispatch(
      trackingEvent(media?.tracking?.urls?.stop, {
        timecode: time ? time : getPlayingTime(),
      }),
    );
  };

  const handleResume = () => {
    if (firstPlay.current) {
      firstPlay.current = false;
      return;
    }
    if (!isLive || recordingSelected) {
      dispatch(
        trackingEvent(media?.tracking?.urls?.resume, {
          timecode: getPlayingTime(),
        }),
      );
    }
  };

  const handleError = () => {
    dispatch(
      trackingEvent(media?.tracking?.urls?.error, {
        timecode: getPlayingTime(),
      }),
    );
  };

  const handleSeek = () => {
    dispatch(
      trackingEvent(media?.tracking?.urls?.seek, {
        timecode: getPlayingTime(),
      }),
    );
  };

  const handleSubChange = (params = {}) => {
    dispatch(
      trackingEvent(media?.tracking?.urls?.dubsubchange, {
        timecode: getPlayingTime(),
        ...params,
      }),
    );
  };

  const handleCredits = () => {
    dispatch(
      trackingEvent(media?.tracking?.urls?.credits, {
        timecode: getPlayingTime(),
      }),
    );
  };

  const handleEnd = time => {
    if (!isLive) {
      dispatch(
        trackingEvent(trackingData?.current?.urls?.completion, {
          timecode: time ? time : getPlayingTime(),
        }),
      );
    }
  };

  const handleEpisodeChange = () => {
    if (isLive === false) {
      dispatch(
        trackingEvent(trackingData?.current?.urls?.episodechange, {
          timecode: getPlayingTime(),
        }),
      );
    }
    console.log('HandleEpisodeChange', handleEpisodeChange);
  };

  useEffect(
    () => {
      if (!media || !isLive) return;
      handleAttach();
    },
    [media, isLive],
  );

  const getPlayingTime = () => parseInt(videoTagRef?.currentTime, 10);

  useEffect(() => () => clearInterval(tickInterval?.current), []);

  useEffect(
    () => {
      if (!videoTagRef || !media) return;
      trackingData.current = media?.tracking;

      const updateTime = () => {
        if (!initialTime.current || initialTime?.current === 0) {
          initialTime.current = videoTagRef?.currentTime;
        }
      };

      /* tickInterval.current = setInterval(
        handleTick,
        trackingData?.current?.policies?.tick_interval * 1000,
      ); */

      videoTagRef.addEventListener('loadeddata', handleStart);
      videoTagRef.addEventListener('pause', handlePause);
      videoTagRef.addEventListener('play', handleResume);
      videoTagRef.addEventListener('timeupdate', updateTime);
      videoTagRef.addEventListener('error', handleError);

      // eslint-disable-next-line consistent-return
      return () => {
        initialTime.current = 0;
        videoTagRef.removeEventListener('loadeddata', handleStart);
        videoTagRef.removeEventListener('pause', handlePause);
        videoTagRef.removeEventListener('play', handleResume);
        videoTagRef.removeEventListener('timeupdate', updateTime);
        videoTagRef.removeEventListener('error', handleError);
      };
    },
    [videoTagRef, JSON.stringify(media), JSON.stringify(pbi)],
  );

  useEffect(
    () => {
      if (!videoTagRef || !media) return;

      tickInterval.current = setInterval(() => {
        handleTick(
          getTickLanguageParams({
            isLive,
            currentAudioLanguage,
          }),
        );
      }, trackingData.current?.policies?.tick_interval * 1000);

      return () => {
        clearInterval(tickInterval.current);
      };
    },
    [
      videoTagRef,
      JSON.stringify(media),
      JSON.stringify(pbi),
      currentAudioLanguage,
    ],
  );

  useEffect(
    () => {
      firstPlay.current = true;
    },
    [groupId],
  );

  return {
    handleStart,
    handlePause,
    handleTick,
    handleError,
    handleSeek,
    handleSubChange,
    handleCredits,
    handleEnd,
    handleStop,
    handleEpisodeChange,
    handleResume,
  };
};

export default useTracking;
